import { api } from '@goaltree/backend/convex/_generated/api'
import { HABIT_LEVELS } from '@goaltree/backend/convex/lib/progression'
import { SidebarInset } from '@goaltree/ui/components/sidebar'
import { gravatarUrl } from '@goaltree/ui/lib/gravatar'
import { cn } from '@goaltree/ui/lib/utils'
import { createFileRoute } from '@tanstack/react-router'
import { usePaginatedQuery, useQuery } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import { Award, Crown } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AppSidebar } from '@/components/app-sidebar'
import { BadgeMedal } from '@/components/badge-medal'
import { CanopyAvatar } from '@/components/canopy-avatar'
import { FollowButton } from '@/components/follow-button'
import { levelIcon } from '@/components/habit-level-chip'
import { LeaderboardUserSheet } from '@/components/leaderboard-user-sheet'
import { MobileScreenHeader } from '@/components/mobile-screen-header'
import { PersistentSidebarProvider as SidebarProvider } from '@/components/persistent-sidebar-provider'
import { SiteHeader } from '@/components/site-header'
import { FreezeIcon, StreakFlame } from '@/components/streak-icons'
import { UserAvatar } from '@/components/user-avatar'
import { useScrollTitle } from '@/hooks/use-scroll-title'
import { authClient } from '@/lib/auth-client'

export const Route = createFileRoute('/_authenticated/leaderboard')({
  head: () => ({
    meta: [{ title: 'Leaderboard — Goaltree' }, { name: 'robots', content: 'noindex, nofollow' }],
  }),
  // ?user=<id> opens that person's sheet (new-follower notifications).
  validateSearch: (s: Record<string, unknown>): { user?: string } =>
    typeof s.user === 'string' && s.user ? { user: s.user } : {},
  component: RouteComponent,
})

type Entry = {
  userId: string
  handle?: string | null
  name: string
  image: string | null
  streak: number
  freezes: number
  goals?: number
  tasksThisWeek?: number
  weekCount?: number
  badgeCount?: number
  showcase?: string[]
  topLevel?: number
  rank: number
  isCurrentUser: boolean
}

type Mode = 'week' | 'streak' | 'badges'
const MODES: Mode[] = ['week', 'streak', 'badges']

// The number a board ranks by, for podium/row/pill display.
function modeValue(e: Entry, mode: Mode) {
  if (mode === 'week') return String(e.weekCount ?? 0)
  if (mode === 'badges')
    return (
      <>
        <Award className="inline size-3.5 -translate-y-px" strokeWidth={2.5} /> {e.badgeCount ?? 0}
      </>
    )
  return (
    <>
      <StreakFlame /> {e.streak}
    </>
  )
}

const PODIUM = [
  { place: 2, size: 46, color: '#cbd5e1', step: 36 },
  { place: 1, size: 58, color: '#facc15', step: 52 },
  { place: 3, size: 42, color: '#d97706', step: 26 },
] as const

function Avatar({
  name,
  image,
  userId,
  crown,
}: {
  name: string
  image: string | null
  userId?: string
  crown?: boolean
}) {
  // Leaderboard entries carry no email, so this resolves real photo → DiceBear.
  return (
    <span className="relative shrink-0">
      <CanopyAvatar userId={userId}>
        <UserAvatar
          name={name}
          image={image}
          className="h-8 w-8 shrink-0 rounded-full object-cover text-xs"
        />
      </CanopyAvatar>
      {crown && <Crowned className="-top-2.5 -right-1.5 size-4" />}
    </span>
  )
}

function Crowned({ className }: { className?: string }) {
  return (
    <Crown
      aria-hidden
      className={cn('absolute z-10 rotate-[20deg] text-[#FFB020] drop-shadow-sm', className)}
      fill="#FFB020"
      strokeWidth={2}
    />
  )
}

// Highest habit stage plus up to three showcased badges; extras collapse to +N.
function Flair({
  entry,
  center,
  inverted,
}: {
  entry: Entry
  center?: boolean
  inverted?: boolean
}) {
  const { t } = useTranslation()
  const showcase = (entry.showcase ?? []).slice(0, 3)
  const level = entry.topLevel ?? 0
  const def = level > 0 ? HABIT_LEVELS[Math.min(level, HABIT_LEVELS.length) - 1] : null
  const total = Math.max(entry.badgeCount ?? 0, showcase.length)
  const extra = total - showcase.length
  if (!def && showcase.length === 0) return null
  const LevelIcon = levelIcon(level)
  const name = def ? t(`progression.level.${def.name}`) : ''
  return (
    <span
      className={cn(
        'flex min-w-0 items-center gap-1.5 text-[11px] leading-none',
        center && 'justify-center',
        inverted ? 'text-background/60' : 'text-muted-foreground',
      )}
    >
      {def && (
        <span
          title={t('progression.levelChip', { name, level })}
          className="inline-flex min-w-0 items-center gap-1 font-mono"
        >
          <LevelIcon className="size-2.5 shrink-0" strokeWidth={2.5} />
          <span className="truncate">{name}</span>
        </span>
      )}
      {showcase.length > 0 && (
        <span className="inline-flex shrink-0 items-center gap-0.5">
          {showcase.map((id, i) => (
            <BadgeMedal
              key={id}
              id={id}
              size="xs"
              className={cn(i > 0 && 'hidden sm:inline-flex')}
            />
          ))}
          {extra > 0 && <span className="hidden font-mono sm:inline">+{extra}</span>}
          {total > 1 && <span className="font-mono sm:hidden">+{total - 1}</span>}
        </span>
      )}
    </span>
  )
}

function useDisplayName() {
  const { t } = useTranslation()
  return (e: Pick<Entry, 'name' | 'handle' | 'isCurrentUser'>) =>
    e.isCurrentUser
      ? t('leaderboard.you')
      : (!e.name || e.name === 'Anonymous') && e.handle
        ? `@${e.handle}`
        : e.name || 'Anonymous'
}

// Bronze rises first, gold last, so the tallest step lands as the finale.
const PODIUM_DELAY: Record<number, number> = { 3: 0, 2: 140, 1: 280 }

function Podium({
  top,
  mode,
  meRef,
  onClick,
}: {
  top: Entry[]
  mode: Mode
  meRef: React.Ref<HTMLDivElement>
  onClick: (e: Entry) => void
}) {
  const { t } = useTranslation()
  const displayName = useDisplayName()
  // Drop the animation classes once played so a later re-render can't replay them.
  const [animate, setAnimate] = useState(true)
  useEffect(() => {
    const id = setTimeout(() => setAnimate(false), 1400)
    return () => clearTimeout(id)
  }, [])
  const rise = (place: number) =>
    animate
      ? { className: 'podium-rise', style: { animationDelay: `${PODIUM_DELAY[place]}ms` } }
      : { className: '', style: {} }
  const pop = (place: number) =>
    animate
      ? { className: 'podium-pop', style: { animationDelay: `${PODIUM_DELAY[place] + 320}ms` } }
      : { className: '', style: {} }
  return (
    <div className="mb-6 grid grid-cols-3 items-end gap-2">
      {PODIUM.map(({ place, size, color, step }) => {
        const e = top[place - 1]
        if (!e)
          return (
            <div key={place} className="flex min-w-0 flex-col items-center text-center">
              <div
                className={cn('flex flex-col items-center', pop(place).className)}
                style={pop(place).style}
              >
                <div
                  className="shrink-0 rounded-full border-[3px] border-dashed border-foreground/15"
                  style={{ width: size, height: size }}
                />
                <p className="mt-1.5 text-xs font-bold text-foreground/25">—</p>
              </div>
              <div
                className={cn(
                  'mt-1.5 flex w-full justify-center rounded-t-lg bg-foreground/[0.06] pt-1 text-sm font-black text-foreground/25',
                  rise(place).className,
                )}
                style={{ height: step, ...rise(place).style }}
              >
                {place}
              </div>
            </div>
          )
        const weekCount = e.weekCount ?? 0
        return (
          <div
            key={place}
            ref={e.isCurrentUser ? meRef : undefined}
            onClick={() => onClick(e)}
            className="flex min-w-0 cursor-pointer flex-col items-center text-center transition-opacity hover:opacity-90"
          >
            <div
              className={cn('flex w-full min-w-0 flex-col items-center', pop(place).className)}
              style={pop(place).style}
            >
              <div className="relative">
                <CanopyAvatar userId={e.userId}>
                  <div
                    className="shrink-0 overflow-hidden rounded-full"
                    style={{ width: size, height: size, border: `3px solid ${color}` }}
                  >
                    <UserAvatar
                      name={e.name}
                      image={e.image}
                      size={size}
                      className="block h-full w-full object-cover"
                    />
                  </div>
                </CanopyAvatar>
                {place === 1 && <Crowned className="-top-4 -right-2 size-6" />}
              </div>
              <p
                className={cn(
                  'mt-1.5 w-full truncate text-xs font-bold text-foreground',
                  e.isCurrentUser && 'capitalize',
                )}
              >
                {displayName(e)}
              </p>
              {mode === 'week' ? (
                <p className="text-sm font-black text-foreground">
                  {weekCount}{' '}
                  <span className="text-[10px] font-medium text-muted-foreground">
                    {t('leaderboard.tasksUnit', { count: weekCount })}
                  </span>
                </p>
              ) : (
                <p className="text-sm font-black text-foreground">{modeValue(e, mode)}</p>
              )}
              <div className="mt-1 w-full">
                <Flair entry={e} center />
              </div>
            </div>
            <div
              className={cn(
                'mt-1.5 flex w-full justify-center rounded-t-lg pt-1 text-sm font-black text-black/70',
                rise(place).className,
              )}
              style={{ height: step, backgroundColor: color, ...rise(place).style }}
            >
              {place}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function Row({
  entry,
  mode,
  innerRef,
  onClick,
  unranked,
}: {
  entry: Entry
  mode: Mode
  unranked?: 'quiet' | 'inactive'
  innerRef?: React.Ref<HTMLDivElement>
  onClick?: (e: Entry) => void
}) {
  const { t } = useTranslation()
  const displayName = useDisplayName()
  const isTop3 = !unranked && entry.rank <= 3
  const rankOrdinals = [t('leaderboard.rank1'), t('leaderboard.rank2'), t('leaderboard.rank3')]
  const me = entry.isCurrentUser
  const dim = !!unranked && !me

  return (
    <div
      ref={innerRef}
      onClick={() => onClick?.(entry)}
      className={cn(
        'flex cursor-pointer items-center gap-3 py-3 transition-colors',
        // Bleed the highlight outward so its content still lines up with plain rows.
        me
          ? '-mx-2 rounded-xl bg-foreground px-3 hover:bg-foreground/90'
          : 'px-1 hover:bg-foreground/[0.03]',
      )}
    >
      <span
        className={cn(
          'w-7 shrink-0 text-right font-black',
          isTop3 ? 'text-sm' : 'text-base',
          me ? 'text-background' : dim ? 'text-foreground/40' : 'text-foreground',
        )}
      >
        {isTop3 ? rankOrdinals[entry.rank - 1] : entry.rank}
      </span>
      <div className={cn('flex min-w-0 flex-1 items-center gap-3', dim && 'opacity-60')}>
        <Avatar
          name={entry.name}
          image={entry.image}
          userId={entry.userId}
          crown={!unranked && entry.rank === 1}
        />
        <div className="min-w-0 flex-1">
          <p
            className={cn(
              'truncate text-sm font-semibold',
              me ? 'text-background' : 'text-foreground',
              entry.isCurrentUser && 'capitalize',
            )}
          >
            {displayName(entry)}
          </p>
          <div className="mt-1">
            <Flair entry={entry} inverted={me} />
          </div>
        </div>
        {!unranked && mode === 'streak' && entry.freezes > 0 && (
          <span className="text-sm" title={t('today.streakFreezeTooltip')}>
            {Array.from({ length: entry.freezes }, (_, i) => (
              <FreezeIcon key={i} />
            ))}
          </span>
        )}
        {unranked !== 'inactive' && (
          <span
            className={cn('shrink-0 text-sm font-bold', me ? 'text-background' : 'text-foreground')}
          >
            {modeValue(entry, unranked === 'quiet' ? 'streak' : mode)}
          </span>
        )}
      </div>
      {!entry.isCurrentUser && <FollowButton handle={entry.handle ?? null} userId={entry.userId} />}
    </div>
  )
}

// edge: which side of the scroll view your off-screen row is on.
type FloatingState = {
  user: Entry
  mode: Mode
  scrollToMe: () => void
  edge: 'top' | 'bottom'
} | null

const PAGE_SIZE = 30

// Loads the next page as the end of the list approaches the viewport.
function LoadMoreSentinel({
  canLoad,
  loading,
  onLoadMore,
}: {
  canLoad: boolean
  loading: boolean
  onLoadMore: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el || !canLoad) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) onLoadMore()
      },
      { rootMargin: '400px 0px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [canLoad, onLoadMore])
  return (
    <div ref={ref} className="flex justify-center py-4">
      {loading && (
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted border-t-foreground" />
      )}
    </div>
  )
}

function isoWeekOf(mondayKey: string) {
  const thursday = new Date(`${mondayKey}T00:00:00Z`).getTime() + 3 * 86_400_000
  const yearStart = Date.UTC(new Date(thursday).getUTCFullYear(), 0, 1)
  return Math.floor((thursday - yearStart) / (7 * 86_400_000)) + 1
}

function useNow(intervalMs: number) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs)
    return () => clearInterval(id)
  }, [intervalMs])
  return now
}

function WeekResetLine({ weekKey, weekEndsAt }: { weekKey: string; weekEndsAt: number }) {
  const { t } = useTranslation()
  const now = useNow(60_000)
  const left = Math.max(0, weekEndsAt - now)
  return (
    <>
      {t('leaderboard.weekResets', {
        week: isoWeekOf(weekKey),
        d: Math.floor(left / 86_400_000),
        h: Math.floor((left % 86_400_000) / 3_600_000),
      })}
    </>
  )
}

function Spinner() {
  return (
    <div className="flex justify-center py-6">
      <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted border-t-foreground" />
    </div>
  )
}

function ListSkeleton() {
  return (
    <div>
      <div className="mb-6 grid grid-cols-3 items-end gap-2">
        {PODIUM.map(({ place, size, step }) => (
          <div key={place} className="flex flex-col items-center">
            <div
              className="animate-pulse rounded-full bg-muted/30"
              style={{ width: size, height: size }}
            />
            <div className="mt-1.5 h-3 w-14 animate-pulse rounded bg-muted/30" />
            <div className="mt-1.5 h-3.5 w-9 animate-pulse rounded bg-muted/30" />
            <div className="mt-1 h-2.5 w-12 animate-pulse rounded bg-muted/20" />
            <div
              className="mt-1.5 w-full animate-pulse rounded-t-lg bg-muted/30"
              style={{ height: step }}
            />
          </div>
        ))}
      </div>
      <div className="mb-2 h-3 w-20 animate-pulse rounded bg-muted/30" />
      <div className="divide-y divide-border/40">
        {[64, 48, 72, 56, 40, 66, 52].map((w, i) => (
          <div key={i} className="flex items-center gap-3 px-1 py-3">
            <div className="flex w-7 shrink-0 justify-end">
              <div className="h-4 w-5 animate-pulse rounded bg-muted/30" />
            </div>
            <div className="size-8 shrink-0 animate-pulse rounded-full bg-muted/30" />
            <div className="min-w-0 flex-1">
              <div className="h-3.5 animate-pulse rounded bg-muted/30" style={{ width: `${w}%` }} />
              <div className="mt-1.5 h-2.5 w-2/5 animate-pulse rounded bg-muted/20" />
            </div>
            <div className="h-4 w-10 animate-pulse rounded bg-muted/30" />
            <div className="h-6 w-16 animate-pulse rounded-full bg-muted/30" />
          </div>
        ))}
      </div>
    </div>
  )
}

type MyRank = FunctionReturnType<typeof api.leaderboard.getMyLeaderboardRank> | undefined
type Meta = FunctionReturnType<typeof api.leaderboard.getLeaderboardMeta> | undefined

// Stays mounted (collapsed off-screen) after first visit so its subscriptions and loaded pages survive tab switches.
function Board({
  mode,
  active,
  myRank,
  meta,
  sessionImage,
  onSelect,
  onFloatingChange,
  onHasUserChange,
  swiping,
}: {
  mode: Mode
  active: boolean
  // Rows slide sideways mid-swipe; don't read that as "scrolled out of view".
  swiping: boolean
  myRank: MyRank
  meta: Meta
  sessionImage: string | null
  onSelect: (e: Entry) => void
  onFloatingChange: (s: FloatingState) => void
  onHasUserChange: (hasUser: boolean) => void
}) {
  const { t } = useTranslation()
  const currentUserRef = useRef<HTMLDivElement>(null)
  const opts = { initialNumItems: PAGE_SIZE }

  const weekPages = usePaginatedQuery(
    api.leaderboard.getWeeklyLeaderboardPage,
    mode === 'week' ? {} : 'skip',
    opts,
  )
  const streakPages = usePaginatedQuery(
    api.leaderboard.getGlobalLeaderboardPage,
    mode === 'streak' ? {} : 'skip',
    opts,
  )
  const badgePages = usePaginatedQuery(
    api.leaderboard.getBadgesLeaderboardPage,
    mode === 'badges' ? {} : 'skip',
    opts,
  )
  const pages = mode === 'week' ? weekPages : mode === 'streak' ? streakPages : badgePages
  const isLoading = pages.status === 'LoadingFirstPage'
  const boardDone = pages.status === 'Exhausted'

  const quietPages = usePaginatedQuery(
    api.leaderboard.getQuietThisWeekPage,
    mode === 'week' && boardDone ? {} : 'skip',
    opts,
  )
  const quietDone = mode !== 'week' || quietPages.status === 'Exhausted'
  const showInactive =
    mode !== 'badges' && boardDone && quietDone && !!meta && meta.inactiveCount > 0
  const inactivePages = usePaginatedQuery(
    api.leaderboard.getInactiveLeaderboardPage,
    showInactive ? {} : 'skip',
    opts,
  )

  // Ranked board first, then quiet, then inactive; ranks continue across all three.
  // Dedup by userId and keep the flag on only the first isCurrentUser row.
  const seen = new Set<string>()
  let foundCurrentUser = false
  const rank = (list: Omit<Entry, 'rank'>[], base: number): Entry[] =>
    list
      .filter((e) => {
        if (seen.has(e.userId)) return false
        seen.add(e.userId)
        return true
      })
      .map((e, i) => {
        const isCurrentUser = e.isCurrentUser && !foundCurrentUser
        if (isCurrentUser) foundCurrentUser = true
        return {
          ...e,
          rank: base + i + 1,
          isCurrentUser,
          image: isCurrentUser ? (e.image ?? sessionImage) : e.image,
        }
      })
  const entries = isLoading ? [] : rank(pages.results, 0)
  const quietEntries = mode === 'week' && boardDone ? rank(quietPages.results, entries.length) : []
  const inactiveEntries = showInactive
    ? rank(inactivePages.results, entries.length + quietEntries.length)
    : []

  const top3 = entries.slice(0, 3)
  const listEntries = entries.slice(3)
  const loadedMe = entries.find((e) => e.isCurrentUser) ?? null
  const extraMe =
    quietEntries.find((e) => e.isCurrentUser) ??
    inactiveEntries.find((e) => e.isCurrentUser) ??
    null
  const myEntry = myRank ? { ...myRank.entry, image: myRank.entry.image ?? sessionImage } : null
  // Below the loaded pages, fall back to the server-computed rank so "you" still pins.
  const serverRank =
    mode === 'week'
      ? myRank?.weekRank
      : mode === 'badges'
        ? myRank?.badgesRank
        : myRank?.active
          ? myRank.entry.rank
          : null
  const currentUser: Entry | null =
    loadedMe ?? extraMe ?? (serverRank != null && myEntry ? { ...myEntry, rank: serverRank } : null)
  // Your row may still arrive in a page that's loading; until then the server
  // rank alone would pin a "You" card that vanishes a moment later.
  const rowPending =
    isLoading ||
    (mode === 'week' && boardDone && quietPages.status === 'LoadingFirstPage') ||
    (mode !== 'badges' && boardDone && quietDone && meta === undefined) ||
    (showInactive && inactivePages.status === 'LoadingFirstPage')
  const pendingScrollRef = useRef(false)

  function scrollToMe() {
    if (currentUserRef.current) {
      currentUserRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    // Not loaded yet — pull pages down to your row, then scroll once it renders.
    if (serverRank != null && pages.status === 'CanLoadMore') {
      pendingScrollRef.current = true
      pages.loadMore(Math.max(PAGE_SIZE, serverRank - pages.results.length + 5))
    }
  }

  useEffect(() => {
    if (!pendingScrollRef.current || !loadedMe) return
    pendingScrollRef.current = false
    requestAnimationFrame(() =>
      currentUserRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
    )
  }, [loadedMe?.userId])

  // Reserve bottom space whenever a pinnable "you" exists — a stable signal that
  // doesn't flip as the row scrolls in/out of view. Toggling the scroll
  // container's padding on the transient floating state would shift content and
  // re-trigger the intersection observer, causing a scroll flicker/jerk.
  useEffect(() => {
    if (active) onHasUserChange(!!currentUser)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!currentUser, active])

  useEffect(() => {
    if (!active || swiping) return
    const el = currentUserRef.current
    if (!currentUser) {
      onFloatingChange(null)
      return
    }
    if (!el) {
      onFloatingChange(rowPending ? null : { user: currentUser, mode, scrollToMe, edge: 'bottom' })
      return
    }
    const observer = new IntersectionObserver(
      ([entry]) =>
        onFloatingChange(
          entry.isIntersecting
            ? null
            : {
                user: currentUser,
                mode,
                scrollToMe,
                edge:
                  entry.boundingClientRect.top < (entry.rootBounds?.top ?? 0) ? 'top' : 'bottom',
              },
        ),
      { threshold: 0, rootMargin: '-140px 0px -140px 0px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [
    active,
    swiping,
    currentUser?.userId,
    currentUser?.rank,
    currentUser?.streak,
    currentUser?.weekCount,
    currentUser?.badgeCount,
    !!loadedMe,
    !!extraMe,
    pages.results.length,
    rowPending,
  ])

  const empty =
    mode === 'week'
      ? [t('leaderboard.weekEmpty'), t('leaderboard.weekEmptyDesc')]
      : mode === 'badges'
        ? [t('leaderboard.badgesEmpty'), t('leaderboard.badgesEmptyDesc')]
        : [t('leaderboard.noActivity'), t('leaderboard.noActivityDesc')]

  const rowRef = (e: Entry) => (e.isCurrentUser ? currentUserRef : undefined)
  const tail = showInactive ? inactivePages : mode === 'week' && boardDone ? quietPages : pages

  return (
    <div>
      {isLoading && <ListSkeleton />}

      {!isLoading && entries.length === 0 && (
        <div
          className={cn(
            'text-center',
            // Quiet/inactive members still list below, so don't push them off-screen.
            quietEntries.length + inactiveEntries.length > 0 ? 'pt-4 pb-6' : 'py-16',
          )}
        >
          <p className="text-xl font-bold text-foreground">{empty[0]}</p>
          <p className="mt-2 text-sm text-muted-foreground">{empty[1]}</p>
        </div>
      )}

      {!isLoading && entries.length > 0 && (
        <Podium top={top3} mode={mode} meRef={currentUserRef} onClick={onSelect} />
      )}

      {listEntries.length + quietEntries.length + inactiveEntries.length > 0 && (
        <>
          {listEntries.length > 0 && (
            <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-foreground/30">
              {t('leaderboard.rankings')}
            </p>
          )}
          <div className="divide-y divide-border/40">
            {listEntries.map((e) => (
              <Row key={e.userId} entry={e} mode={mode} innerRef={rowRef(e)} onClick={onSelect} />
            ))}
            {quietEntries.map((e) => (
              <Row
                key={e.userId}
                entry={e}
                mode={mode}
                unranked="quiet"
                innerRef={rowRef(e)}
                onClick={onSelect}
              />
            ))}
            {inactiveEntries.map((e) => (
              <Row
                key={e.userId}
                entry={e}
                mode={mode}
                unranked="inactive"
                innerRef={rowRef(e)}
                onClick={onSelect}
              />
            ))}
          </div>
        </>
      )}

      {!isLoading &&
        (tail.status === 'LoadingFirstPage' ? (
          <Spinner />
        ) : (
          <LoadMoreSentinel
            canLoad={active && tail.status === 'CanLoadMore'}
            loading={tail.status === 'LoadingMore'}
            onLoadMore={() => tail.loadMore(PAGE_SIZE)}
          />
        ))}
    </div>
  )
}

function inHorizontalScroller(target: EventTarget | null, root: Element) {
  for (let el = target as Element | null; el && el !== root; el = el.parentElement) {
    if (el.scrollWidth > el.clientWidth && /auto|scroll/.test(getComputedStyle(el).overflowX))
      return true
  }
  return false
}

function LeaderboardContent({
  onFloatingChange,
  onHasUserChange,
}: {
  onFloatingChange: (s: FloatingState) => void
  onHasUserChange: (hasUser: boolean) => void
}) {
  const { t } = useTranslation()
  const [mode, setMode] = useState<Mode>('week')
  const [visited, setVisited] = useState<Mode[]>(['week'])
  const [selectedUser, setSelectedUser] = useState<Entry | null>(null)
  const { user: linkedUser } = Route.useSearch()
  const navigate = Route.useNavigate()
  const [drag, setDrag] = useState<number | null>(null)
  const [settling, setSettling] = useState(false)
  const viewportRef = useRef<HTMLDivElement>(null)
  const indexRef = useRef(0)
  const settleTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const { data: session } = authClient.useSession()

  const myRank = useQuery(api.leaderboard.getMyLeaderboardRank)
  const meta = useQuery(api.leaderboard.getLeaderboardMeta)

  const sessionImage =
    session?.user?.image ?? (session?.user?.email ? gravatarUrl(session.user.email, 96) : null)

  const index = MODES.indexOf(mode)
  indexRef.current = index

  function visit(m: Mode) {
    setVisited((v) => (v.includes(m) ? v : [...v, m]))
  }

  // Panes stay expanded while the track animates, then off-screen ones collapse to h-0.
  function settleTo(i: number) {
    const m = MODES[Math.max(0, Math.min(MODES.length - 1, i))]
    setMode(m)
    visit(m)
    setDrag(null)
    setSettling(true)
    clearTimeout(settleTimer.current)
    settleTimer.current = setTimeout(() => setSettling(false), 300)
  }

  const settleRef = useRef(settleTo)
  settleRef.current = settleTo

  useEffect(() => {
    const el = viewportRef.current
    if (!el) return
    let g: {
      x: number
      y: number
      lastX: number
      lastT: number
      v: number
      w: number
      locked: boolean
    } | null = null

    const onStart = (e: TouchEvent) => {
      const t = e.touches[0]
      g =
        e.touches.length === 1 && !inHorizontalScroller(e.target, el)
          ? {
              x: t.clientX,
              y: t.clientY,
              lastX: t.clientX,
              lastT: e.timeStamp,
              v: 0,
              w: el.clientWidth,
              locked: false,
            }
          : null
    }
    const onMove = (e: TouchEvent) => {
      if (!g) return
      const t = e.touches[0]
      const dx = t.clientX - g.x
      const dy = t.clientY - g.y
      if (!g.locked) {
        if (Math.abs(dx) <= 8 && Math.abs(dy) <= 8) return
        if (Math.abs(dx) <= Math.abs(dy)) {
          g = null
          return
        }
        g.locked = true
        clearTimeout(settleTimer.current)
      }
      if (e.cancelable) e.preventDefault()
      const dt = e.timeStamp - g.lastT
      if (dt > 0) g.v = 0.8 * ((t.clientX - g.lastX) / dt) + 0.2 * g.v
      g.lastX = t.clientX
      g.lastT = e.timeStamp
      const i = indexRef.current
      const neighbour = MODES[i + (dx < 0 ? 1 : -1)]
      if (neighbour) visit(neighbour)
      setDrag(neighbour ? dx : dx / 3)
    }
    const onEnd = (e: TouchEvent) => {
      if (!g?.locked) {
        g = null
        return
      }
      const dx = g.lastX - g.x
      const v = e.timeStamp - g.lastT < 100 ? g.v : 0
      const flung = Math.abs(v) > 0.5 && Math.sign(v) === Math.sign(dx)
      const go = e.type === 'touchend' && (Math.abs(dx) > g.w * 0.25 || flung)
      settleRef.current(indexRef.current + (go ? (dx < 0 ? 1 : -1) : 0))
      g = null
    }

    el.addEventListener('touchstart', onStart, { passive: true })
    el.addEventListener('touchmove', onMove, { passive: false })
    el.addEventListener('touchend', onEnd)
    el.addEventListener('touchcancel', onEnd)
    return () => {
      el.removeEventListener('touchstart', onStart)
      el.removeEventListener('touchmove', onMove)
      el.removeEventListener('touchend', onEnd)
      el.removeEventListener('touchcancel', onEnd)
      clearTimeout(settleTimer.current)
    }
  }, [])

  const width = viewportRef.current?.clientWidth || 1
  const pillPos = Math.max(0, Math.min(MODES.length - 1, index - (drag ?? 0) / width))
  const expanded = drag !== null || settling

  const pct =
    myRank?.active && !myRank.capped && meta && meta.total > 0
      ? Math.min(99, Math.max(0, Math.round(((meta.total - myRank.entry.rank) / meta.total) * 100)))
      : null

  const subLine =
    mode === 'streak' && pct !== null ? (
      t('leaderboard.aheadOf', { pct })
    ) : mode === 'week' && meta ? (
      <WeekResetLine weekKey={meta.weekKey} weekEndsAt={meta.weekEndsAt} />
    ) : mode === 'badges' ? (
      t('leaderboard.badgesSub')
    ) : null

  return (
    <div className="bg-background px-4 pt-4 pb-4">
      <div className="mx-auto max-w-(--page-col)">
        <div className="mb-5">
          <h1 className="text-3xl font-black text-foreground">{t('nav.leaderboard')}</h1>
          {subLine && <p className="mt-1 font-mono text-xs text-muted-foreground">{subLine}</p>}
        </div>

        <div className="relative mb-6 flex rounded-full bg-foreground/10 p-1">
          <div
            className={cn(
              'pointer-events-none absolute inset-y-1 left-1 rounded-full bg-foreground shadow-sm transition-transform duration-250 ease-out motion-reduce:transition-none',
              drag !== null && 'transition-none',
            )}
            style={{
              width: `calc(${100 / MODES.length}% - ${8 / MODES.length}px)`,
              transform: `translateX(${pillPos * 100}%)`,
            }}
          />
          {MODES.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => settleTo(MODES.indexOf(m))}
              aria-pressed={mode === m}
              className={cn(
                'relative z-10 flex flex-1 cursor-pointer items-center justify-center gap-1 truncate rounded-full px-1 py-2 text-sm font-semibold transition-colors duration-150',
                mode === m ? 'text-background' : 'text-foreground/40 hover:text-foreground/60',
              )}
            >
              {m === 'streak' && <StreakFlame />}
              {m === 'badges' && <Award className="size-3.5 shrink-0" strokeWidth={2.5} />}
              {m === 'week'
                ? t('leaderboard.thisWeekTab')
                : m === 'streak'
                  ? t('leaderboard.streakTab')
                  : t('leaderboard.badgesTab')}
            </button>
          ))}
        </div>

        {/* Padded so the clip leaves room for the "You" row's bleed (-mx-2). */}
        <div ref={viewportRef} className="-mx-2 min-h-[60svh] touch-pan-y overflow-x-clip px-2">
          <div
            className={cn(
              'flex items-start transition-transform duration-250 ease-out motion-reduce:transition-none',
              drag !== null && 'transition-none',
            )}
            style={{
              transform: `translateX(calc(${-index * 100}% + ${drag ?? 0}px))`,
            }}
          >
            {MODES.map((m) => (
              <div
                key={m}
                inert={m !== mode}
                className={cn('w-full shrink-0', m !== mode && !expanded && 'h-0 overflow-hidden')}
              >
                {visited.includes(m) ? (
                  <Board
                    mode={m}
                    active={m === mode}
                    myRank={myRank}
                    meta={meta}
                    sessionImage={sessionImage}
                    onSelect={setSelectedUser}
                    onFloatingChange={onFloatingChange}
                    onHasUserChange={onHasUserChange}
                    swiping={expanded}
                  />
                ) : (
                  expanded && <ListSkeleton />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <LeaderboardUserSheet
        userId={selectedUser?.userId ?? linkedUser ?? null}
        name={selectedUser?.name ?? ''}
        image={selectedUser?.image ?? null}
        onClose={() => {
          setSelectedUser(null)
          if (linkedUser) void navigate({ search: {}, replace: true })
        }}
      />
    </div>
  )
}

function FloatingUserBadge({ floating }: { floating: FloatingState }) {
  const { t } = useTranslation()
  if (!floating) return null
  const { user, mode } = floating
  return (
    <div
      className={
        floating.edge === 'top'
          ? 'pointer-events-none absolute inset-x-0 top-2 z-20 flex justify-center px-4'
          : 'pointer-events-none absolute inset-x-0 bottom-[calc(5rem+env(safe-area-inset-bottom,0px))] z-20 flex justify-center px-4 lg:bottom-20'
      }
    >
      <button
        type="button"
        onClick={floating.scrollToMe}
        className="cursor-pointer pointer-events-auto flex w-full max-w-(--page-col) items-center gap-3 rounded-2xl bg-foreground px-3 py-3 shadow-lg transition-opacity hover:opacity-90"
      >
        <span className="shrink-0 text-sm font-black text-background">
          {t('leaderboard.rankOrdinal', { count: user.rank, ordinal: true })}
        </span>
        <Avatar name={user.name} image={user.image} userId={user.userId} />
        <span className="min-w-0 flex-1 truncate text-left text-sm font-semibold capitalize text-background">
          {t('leaderboard.you')}
        </span>
        <span className="shrink-0 text-sm font-bold text-background">
          {mode === 'week'
            ? t('leaderboard.tasksCount', { count: user.weekCount ?? 0 })
            : modeValue(user, mode)}
        </span>
      </button>
    </div>
  )
}

function RouteComponent() {
  const { t } = useTranslation()
  const { onScroll } = useScrollTitle(t('nav.leaderboard'))
  const [floating, setFloating] = useState<FloatingState>(null)
  const [hasUser, setHasUser] = useState(false)

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset className="h-svh overflow-hidden bg-background">
        <MobileScreenHeader title={t('nav.leaderboard')} showInbox={false} showCoach />
        <SiteHeader className="hidden lg:flex" />
        <div className="relative flex min-h-0 flex-1 flex-col">
          <div
            className={`flex-1 overflow-y-auto ${hasUser ? 'pb-36 lg:pb-20' : 'pb-16 lg:pb-0'}`}
            onScroll={onScroll}
            data-scroll-reset
            data-scroll-restoration-id="tab-scroll"
          >
            <LeaderboardContent onFloatingChange={setFloating} onHasUserChange={setHasUser} />
          </div>
          <FloatingUserBadge floating={floating} />
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}
